// ── Welle D3b: Heute-Board am PC (ab 1024 px) ──
// react-grid-layout: 4 Spalten, Zeilen 176 px, Abstand 18 px, vertikales Nachrücken.
// Verschieben/Größe ändern nur im Bearbeiten-Modus (Griff bzw. Ecke). Gespeichert wird nach
// dem Loslassen bzw. nach Menü-Aktionen — nie aus onLayoutChange (feuert auch beim Laden).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactGridLayout, { useContainerWidth, verticalCompactor } from 'react-grid-layout';
import type { Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { MIcon } from '../../../lib/micon';
import { toast } from '../../../lib/toast';
import { confirm } from '../../../lib/confirm';
import { Button } from '../../../components/ld/Button';
import { WidgetHuelle } from './WidgetHuelle';
import { WidgetMenuPc } from './WidgetMenu';
import { WIDGET_META, hatMeta } from './widget-meta';
import type { PcItem, PcLayout, WidgetDef, RglItem } from './layout-logik';
import { SPALTEN, aendereGroesse, ersteFreieStelle, kompaktiere, vonRgl, zuRgl } from './layout-logik';
import type { WidgetInhalt } from './WidgetInhalt';

export const ZEILENHOEHE = 176;
export const ABSTAND = 18;

type Props = {
  layout: PcLayout;
  defs: Map<string, WidgetDef>;
  inhalte: Record<string, WidgetInhalt>;
  bearbeiten: boolean;
  setBearbeiten: (b: boolean) => void;
  speichere: (l: PcLayout, sofort?: boolean) => void;
  zuruecksetzen: () => Promise<void>;
};

export function BoardPc({ layout, defs, inhalte, bearbeiten, setBearbeiten, speichere, zuruecksetzen }: Props) {
  const { width, mounted, containerRef } = useContainerWidth({ measureBeforeMount: true });
  // Nur Widgets mit bekannter Beschreibung und Größe; Unbekanntes wird ignoriert
  const sichtbare = useMemo(() => layout.widgets.filter(w => hatMeta(w.id) && defs.has(w.id)), [layout.widgets, defs]);
  const [rgl, setRgl] = useState<RglItem[]>(() => zuRgl(sichtbare, defs));
  useEffect(() => { setRgl(zuRgl(sichtbare, defs)); }, [sichtbare, defs]);
  const [menu, setMenu] = useState<{ id: string; anker: HTMLButtonElement } | null>(null);
  // Größe während des Ziehens an der Ecke (Plakette zählt live mit, in Rasterschritten)
  const [live, setLive] = useState<{ i: string; w: number; h: number } | null>(null);

  /** Neues Widget-Raster speichern; nicht angezeigte (unbekannte) IDs landen unter „ausgeblendet“ */
  const uebernehmen = useCallback((widgets: PcItem[], ausgeblendet: string[], sofort = false) => {
    const drin = new Set(widgets.map(w => w.id));
    const unbekannt = layout.widgets.filter(w => !sichtbare.some(s => s.id === w.id)).map(w => w.id);
    speichere({
      widgets,
      ausgeblendet: [...ausgeblendet.filter(id => !drin.has(id)), ...unbekannt.filter(id => !ausgeblendet.includes(id))],
    }, sofort);
  }, [layout.widgets, sichtbare, speichere]);

  const nachLoslassen = useCallback((l: Layout) => {
    const neu = vonRgl(l);
    if (JSON.stringify(neu) === JSON.stringify(sichtbare)) return; // nur angeklickt, nichts bewegt
    uebernehmen(neu, layout.ausgeblendet);
  }, [sichtbare, layout.ausgeblendet, uebernehmen]);

  function ausblenden(id: string) {
    const vorher = layout;
    const titel = WIDGET_META[id]?.titel ?? 'Widget';
    uebernehmen(kompaktiere(sichtbare.filter(w => w.id !== id)), [id, ...layout.ausgeblendet], true);
    toast(`${titel} ausgeblendet`, 'info', {
      aktion: { label: 'Rückgängig', onClick: () => speichere(vorher, true) },
      dauerMs: 5000,
    });
  }

  function hinzufuegen(id: string) {
    const def = defs.get(id);
    if (!def) return;
    const w = Math.min(def.pc.standard[0], SPALTEN), h = def.pc.standard[1];
    const { x, y } = ersteFreieStelle(sichtbare, w, h);
    uebernehmen(kompaktiere([...sichtbare, { id, x, y, w, h }]), layout.ausgeblendet.filter(a => a !== id), true);
  }

  function groesse(id: string, w: number, h: number) {
    uebernehmen(aendereGroesse(sichtbare, id, w, h), layout.ausgeblendet, true);
  }

  async function standardWiederherstellen() {
    const ok = await confirm('Standard-Anordnung am PC wiederherstellen? Deine Anordnung geht verloren.', {
      title: 'Zurücksetzen', confirmLabel: 'Zurücksetzen', danger: true,
    });
    if (!ok) return;
    try { await zuruecksetzen(); toast('Standard-Anordnung wiederhergestellt'); }
    catch { toast('Zurücksetzen fehlgeschlagen. Bitte noch einmal versuchen.', 'error'); }
  }

  // Esc beendet den Bearbeiten-Modus (offene Menüs/Dialoge fangen Esc vorher ab)
  useEffect(() => {
    if (!bearbeiten) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) setBearbeiten(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [bearbeiten, setBearbeiten]);

  const schliesseMenu = useCallback(() => setMenu(null), []);
  const ablage = layout.ausgeblendet.filter(id => hatMeta(id) && defs.has(id));
  const menuItem = menu ? sichtbare.find(w => w.id === menu.id) : null;
  const menuDef = menu ? defs.get(menu.id) : null;

  return (
    <div className={`ld-board${bearbeiten ? ' ld-board--edit' : ''}`}>
      {bearbeiten && (
        <div className="ld-board__editkopf" role="region" aria-label="Board anpassen">
          <div>
            <h2 className="ld-board__edittitel">Board anpassen</h2>
            <p className="ld-board__edithinweis">Am Griff ziehen, der Rest rückt nach. Ecke ziehen, die Größe rastet ein. × blendet aus.</p>
            <p className="ld-board__editgilt">Gilt auf jedem PC. Fürs Handy gibt es eine eigene Reihenfolge.</p>
          </div>
          <div className="ld-board__editaktionen">
            <Button variant="ghost" onClick={standardWiederherstellen}>Zurücksetzen</Button>
            <Button variant="primary" onClick={() => setBearbeiten(false)}>Fertig</Button>
          </div>
        </div>
      )}

      <div ref={containerRef} className="ld-board__raster">
        {bearbeiten && (
          <div className="ld-board__spalten" aria-hidden="true">
            {Array.from({ length: SPALTEN }).map((_, i) => <span key={i} />)}
          </div>
        )}
        {sichtbare.length === 0 && !bearbeiten ? (
          <div className="ld-board__leer">
            <MIcon name="dashboard_customize" size={30} />
            <p>Dein Board ist leer. Hol dir Widgets aus der Ablage.</p>
            <Button variant="primary" onClick={() => setBearbeiten(true)}>Board anpassen</Button>
          </div>
        ) : mounted && width > 0 && (
          <ReactGridLayout
            layout={rgl}
            width={width}
            gridConfig={{ cols: SPALTEN, rowHeight: ZEILENHOEHE, margin: [ABSTAND, ABSTAND], containerPadding: [0, 0], maxRows: 40 }}
            dragConfig={{ enabled: bearbeiten, handle: '.ld-w__griff', threshold: 3 }}
            resizeConfig={{
              enabled: bearbeiten, handles: ['se'],
              handleComponent: (axis: string, ref: React.Ref<HTMLElement>) => (
                <span ref={ref as React.Ref<HTMLSpanElement>} className={`react-resizable-handle react-resizable-handle-${axis} ld-w__ecke`} aria-hidden="true" />
              ),
            }}
            compactor={verticalCompactor}
            onLayoutChange={l => setRgl(l.map(({ i, x, y, w, h, minW, minH, maxW, maxH }) => ({ i, x, y, w, h, minW, minH, maxW, maxH })))}
            onDragStop={l => nachLoslassen(l)}
            onResize={(_l, _alt, neu) => { if (neu) setLive({ i: neu.i, w: neu.w, h: neu.h }); }}
            onResizeStop={l => { setLive(null); nachLoslassen(l); }}
          >
            {rgl.map(it => {
              const inhalt = inhalte[it.i];
              return (
                <div key={it.i} className="ld-board__zelle">
                  <WidgetHuelle
                    id={it.i}
                    meta={WIDGET_META[it.i]}
                    bearbeiten={bearbeiten}
                    groesse={{ w: live?.i === it.i ? live.w : it.w, h: live?.i === it.i ? live.h : it.h, geraet: 'pc' }}
                    groesseText={live?.i === it.i ? `${live.w} × ${live.h}` : `${it.w} × ${it.h}`}
                    zusatz={inhalt?.zusatz}
                    leerText={inhalt?.leerText}
                    onMenu={anker => setMenu(m => m?.id === it.i ? null : { id: it.i, anker })}
                    onAusblenden={() => ausblenden(it.i)}
                  >
                    {inhalt?.body ?? null}
                  </WidgetHuelle>
                </div>
              );
            })}
          </ReactGridLayout>
        )}
      </div>

      {bearbeiten && (
        <section className="ld-ablage" aria-labelledby="ld-ablage-titel">
          <div className="ld-ablage__kopf">
            <h3 id="ld-ablage-titel">Ausgeblendete Widgets</h3>
            <span>Mit + aufs Board holen</span>
          </div>
          {ablage.length === 0 ? (
            <p className="ld-ablage__leer">Alle Widgets sind auf dem Board.</p>
          ) : (
            <div className="ld-ablage__liste">
              {ablage.map(id => {
                const m = WIDGET_META[id];
                return (
                  <div key={id} className="ld-ablage__karte">
                    <span className="ld-ablage__icon"><MIcon name={m.icon} size={18} /></span>
                    <span className="ld-ablage__text">
                      <b>{m.titel}</b>
                      <small>{m.beschreibung}</small>
                    </span>
                    <button type="button" className="ld-ablage__plus" aria-label={`${m.titel} hinzufügen`} onClick={() => hinzufuegen(id)}>
                      <MIcon name="add" size={20} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {menu && menuItem && menuDef && (
        <WidgetMenuPc
          anker={menu.anker}
          titel={WIDGET_META[menu.id].titel}
          def={menuDef}
          aktuell={{ w: menuItem.w, h: menuItem.h }}
          onGroesse={(w, h) => groesse(menu.id, w, h)}
          onAusblenden={() => ausblenden(menu.id)}
          onAnpassen={() => setBearbeiten(true)}
          onClose={schliesseMenu}
        />
      )}
    </div>
  );
}
